import { type FC, useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Modal,
  type RequiredChildrenProp,
  type TaskBarProps,
  Text,
  type Timeout,
  type UserPromptRecord,
  useMicrophonePermissions,
  usePubSub,
  useDebug,
} from "@huub-web/ui";
import { useNavigationState, type NavigationContext } from "../../providers";
import {
  useFeedbackAPI,
  useSpeechToText,
  useSuggestions,
  useWebSocket,
  WebSocketChannel,
} from "@huub-web/backend";
import { isString } from "@huub-web/utils";

import { useAppController } from "../../hooks/useAppController";
import { useAppSettings } from "../../hooks/useAppSettings";
import {
  TaskbarControllerContext,
  type TaskbarControllerContextState,
} from "../../contexts/TaskbarControllerContext";

type TaskbarProviderProps = RequiredChildrenProp & {
  //
};

/**
 * The TaskbarProvider component is a component that provides the taskbar context.
 */
export const TaskBarProvider: FC<TaskbarProviderProps> = ({ children }) => {
  const { t } = useTranslation();
  const { log } = useDebug();
  const { stopAll, currentAgentResponse, isAudioContextRunning } =
    useAppController();
  const { isAudioEnabled, toggleAudio } = useAppSettings();
  const { isConnected, isWaitingForAgentResponse, sendMessage } =
    useWebSocket();
  const pubSub = usePubSub();
  const { sideContentVariant, navigationContext, isSideContentOpen } =
    useNavigationState();

  const [lastUserPrompt, setLastUserPrompt] =
    useState<TaskbarControllerContextState["lastUserPrompt"]>(null);
  const [lastUserPromptType, setLastUserPromptType] =
    useState<TaskbarControllerContextState["lastUserPromptType"]>("text");
  const removeUserPromptTimeoutRef = useRef<Timeout | null>(null);

  // Recording TTS
  // -------------

  const { microphonePermissionState, requestMicrophonePermissions } =
    useMicrophonePermissions();
  const [permissionsDeniedModalIsOpen, setPermissionsDeniedModalIsOpen] =
    useState(false);
  const [volume, setVolume] = useState(0);

  const {
    startRecording,
    stopRecording,
    isRecording,
    transcription,
    isProcessingRecording,
  } = useSpeechToText({
    onTranscript: (transcript) => {
      setLastUserPromptType("speech");
      setLastUserPrompt(transcript);
      clearSuggestions();
    },
    onVolume: (volume) => setVolume(volume),
    onStop: () => {
      // TODO: ?
    },
  });

  const handleRecord = () => {
    if (microphonePermissionState === "idle") {
      requestMicrophonePermissions(
        () => startRecording(),
        () => setPermissionsDeniedModalIsOpen(true),
      );
    } else if (microphonePermissionState === "denied") {
      setPermissionsDeniedModalIsOpen(true);
    } else if (microphonePermissionState === "granted") {
      startRecording();
    }
  };

  const handleRecordStop = () => {
    if (isRecording) {
      stopRecording();
    }
  };

  const handleMicrophoneModalClose = () => {
    setPermissionsDeniedModalIsOpen(false);
  };

  const handleAudioToggle = () => {
    if (isAudioEnabled && !isAudioContextRunning) {
      return;
    }

    sendMessage(WebSocketChannel.CONFIG_UPDATE, {
      tts_enabled: !isAudioEnabled,
    });
    toggleAudio();
  };

  // Feedback
  // -------------------

  const { sendFeedback } = useFeedbackAPI();
  const [isFeedbackLoading, setIsFeedbackLoading] = useState(false);
  const [hasFeedbackBeenSend, setHasfeedbackBeenSend] =
    useState<boolean>(false);

  const handleFeedbackSubmit: TaskbarControllerContextState["handleFeedbackSubmit"] =
    (data, close) => {
      setIsFeedbackLoading(true);
      setTimeout(() => {
        if (!currentAgentResponse || !currentAgentResponse.feedback) {
          return;
        }

        sendFeedback(
          {
            comments: data.comments,
            tags: data.tags,
            correctness: data.correctness,
            messageID: currentAgentResponse.id,
            runID: currentAgentResponse.feedback.run_id ?? "",
            threadID: currentAgentResponse.feedback.thread_id ?? "",
          },
          () => {
            // Success
            setIsFeedbackLoading(false);
            setHasfeedbackBeenSend(true);
            close();

            pubSub.publish("snackbar", {
              variant: "success",
              body: t("feedback.result.success"),
            });
          },
          () => {
            // Error
            setIsFeedbackLoading(false);
            close();

            pubSub.publish("snackbar", {
              variant: "error",
              body: t("feedback.result.error"),
            });
          },
        );
      }, 750);
    };

  // Sending messages
  // ----------------

  const sendTaskBarMessage = (
    message: UserPromptRecord,
    type: TaskBarProps["promptType"] = "text",
    context: NavigationContext,
  ) => {
    log("blue", "TaskBarController", "Sending message", message, context);

    if (removeUserPromptTimeoutRef.current) {
      clearTimeout(removeUserPromptTimeoutRef.current);
    }

    setLastUserPromptType(type);
    setLastUserPrompt(isString(message) ? message : message.display);
    clearSuggestions();

    sendMessage(WebSocketChannel.CHAT, {
      prompt: isString(message) ? message : message.prompt,
      displayPrompt: isString(message) ? null : (message.display ?? null),
      context,
    });

    pubSub.publish("onPrompt", {
      value: isString(message) ? message : message.display,
      context: context ?? [],
    });
  };

  useEffect(() => {
    if (isWaitingForAgentResponse === true) {
      clearSuggestions();
    }

    if (
      isWaitingForAgentResponse === true &&
      removeUserPromptTimeoutRef.current
    ) {
      clearTimeout(removeUserPromptTimeoutRef.current);
    } else if (lastUserPrompt && isWaitingForAgentResponse === false) {
      removeUserPromptTimeoutRef.current = setTimeout(
        () => setLastUserPrompt(null),
        2500,
      );
    }
  }, [isWaitingForAgentResponse]);

  const handleMessage = (message: string) => {
    sendTaskBarMessage(message, "text", navigationContext);
  };

  const handleMessageStop = () => {
    stopAll();
  };

  // Loading
  // ------------------

  const isLoading = isWaitingForAgentResponse || isProcessingRecording;

  // Disabled
  // ------------------

  const isDisabled = !isConnected;

  // Suggestions
  // ------------------

  const { suggestions, clearSuggestions } = useSuggestions();

  const handleSuggestionClick = (suggestion: UserPromptRecord) => {
    sendTaskBarMessage(suggestion, "text", navigationContext);
  };

  // Task bar on canvas?
  // ------------------

  const hasCanvasTaskBar =
    sideContentVariant === "compact" && isSideContentOpen;

  // Triggered prompts
  // -------------------

  useEffect(() => {
    const unsubscribe = pubSub.subscribe("sendPrompt", ({ value }) => {
      sendTaskBarMessage(value, "help", navigationContext);
    });

    return () => unsubscribe();
  }, [navigationContext]);

  return (
    <>
      <TaskbarControllerContext.Provider
        value={{
          handleSuggestionClick,
          handleMessage,
          handleMessageStop,
          handleFeedbackSubmit,
          handleAudioToggle,
          handleRecord,
          handleRecordStop,
          hasCanvasTaskBar,
          suggestions,
          isDisabled,
          isRecording,
          transcription,
          isProcessingRecording,
          volume,
          isFeedbackLoading,
          hasFeedbackBeenSend,
          lastUserPrompt,
          lastUserPromptType,
          microphonePermissionState,
          isLoading,
        }}
      >
        {children}
      </TaskbarControllerContext.Provider>

      {microphonePermissionState === "denied" &&
        permissionsDeniedModalIsOpen && (
          <Modal
            placement="center"
            title={t("taskbar.microphonePermissonsDenied.title")}
            onClose={handleMicrophoneModalClose}
            escape
          >
            <Text>{t("taskbar.microphonePermissonsDenied.body")}</Text>
            <div>
              <Button onClick={handleMicrophoneModalClose} variant="secondary">
                {t("taskbar.microphonePermissonsDenied.confirm")}
              </Button>
            </div>
          </Modal>
        )}
    </>
  );
};
